import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import BetTeenTable from "../../casinoComponents/betTeenTable";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame, casinoTitle } from "../../../utils/casinoGame";
import VTeen1DayView from "../vteen/VTeen1DayView";
import RefPlayerCardsOverlay from "../../casinoComponents/videoBox/overlays/RefPlayerCardsOverlay";
import TeenRefResults from "./TeenRefResults";
import { formatTeenOneDayNation } from "../../../utils/casinoMyBets";

// casino/tteen (Tembo Teenpatti 1-day): same table as teen/pteen, but the reference always renders the
// card overlay (.hide-cards while cards are backs), takes R:min-max from the feed and lists A/B/T results.
const TTEEN_TITLE = "Tembo Teenpatti 1-day";
const TTEEN_CARD_GROUPS = [
  { title: "Player A", idx: [0, 2, 4] },
  { title: "Player B", idx: [1, 3, 5] },
];


export default function TeenPatti3() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname);
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;

  useEffect(() => {
    const gameName = extractCasinoGame(pathname);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("API Fetch Error:", err.message);
        setGameData(null); // clear data on error
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const subData = gameData?.data?.data?.sub || [];

  const handleBetClick = (value) => {
    if (!value) return;
    fetchExposure();
    fetchMyBetsData();
  };

  const matchId = gameData?.data?.data?.mid?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  // My Bets: same API as lord-admin – GET /lords/bet-list/casino/open-downline?gameName=...
  const fetchMyBetsData = useCallback(async () => {
    const gameName = extractCasinoGame(pathname);
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          // Main vs Consecutive (both "Player A") for the bet name
          mtype: bet.mtype || "",
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname]);

  // Fetch exposure and My Bets on mount and periodically (My Bets every 5s like lord-admin)
  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => {
        fetchExposure();
      }, 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    const t = setTimeout(fetchMyBetsData, 0);
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => {
      clearTimeout(t);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData]);

  // Last result: same as lord-admin — from getCasinoGameDetails response (lrs) or empty
  const resultData = gameData?.lrs ?? gameData?.data?.data?.lrs ?? gameData?.data?.lrs ?? [];

  // vcasino/teen (API type vteen): the reference renders a different, virtual-casino page
  if (gameName === "vteen") {
    return (
      <VTeen1DayView
        gameName={gameName}
        title="V-Teenpatti 1-day"
        data={gameData?.data?.data}
        exposures={exposures}
        onBetClick={handleBetClick}
        myBets={myBets}
        onViewMore={() => setShowViewMoreModal(true)}
        viewMoreModal={
          <ViewMoreModal
            show={showViewMoreModal}
            onHide={() => setShowViewMoreModal(false)}
            title="View More"
            sectionKey={gameName}
          />
        }
      />
    );
  }

  const isTteen = gameName === "tteen";

  return (
    <CasinoTableLayout tableClass="teenpatti1day" rightSide={<MyBet bets={myBets} formatNation={gameName === "teen" ? formatTeenOneDayNation : undefined} onViewMore={() => setShowViewMoreModal(true)} />}>
      <>
        <CasinoHeading
          name={isTteen ? TTEEN_TITLE : casinoTitle(gameName, "Premium Teenpati 1 Day")}
          roundId={gameData?.data?.data?.mid || gameData?.data?.mid || "157250124095658"}
          placedBetCount={myBets.length}
          activeTab="game"
          onTabChange={() => {}}
        />
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={gameData?.data?.data?.lt}
          gameType={gameName || "teen"}
          resultData={gameData?.data?.data?.card ?? gameData?.data?.card ?? (Array.isArray(gameData?.lrs) && gameData.lrs[0]?.card ? gameData.lrs[0].card : undefined)}
          {...(isTteen
            ? { cardsOverlay: <RefPlayerCardsOverlay card={gameData?.data?.data?.card} groups={TTEEN_CARD_GROUPS} /> }
            : {})}
        />

        <div className="casino-detail">
          <BetTeenTable
            data={subData}
            onBetClick={handleBetClick}
            exposures={exposures}
            myBets={myBets}
            rangesFromData={isTteen}
          />
          <CasinoLastResult
            gameType={gameName || "teen"}
            data={resultData}
            gameData={gameData}
            {...(isTteen ? { resultComponent: <TeenRefResults gameType={gameName} fallbackTitle={TTEEN_TITLE} /> } : {})}
          />
        </div>
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName || 'teen'}
        formatNation={gameName === "teen" ? formatTeenOneDayNation : undefined}
      />
    </CasinoTableLayout>
  );
}
